import { STORE } from '~/constants/strings'
import { useProjectOverviewDataVisualizationsStore } from './projectOverviewDataVisualizations'
import { useWorkforceInsightsDataVisualizationsStore } from './workforceInsightsDataVisualizations'
import {
  EnumerationsExecutiveSummaryStatus,
  EnumerationsProportionalAnalyses,
  type CommandsGenerateExecutiveSummaryRequest,
  type CommandsStoreScreenshotsRequest,
} from 'client/sratool'
import type {
  AnalysisOption,
  AnalysisSelection,
  ExecutiveSummaryPersonnelActivityAnalysis,
  FrequencyOfStatisticalImpactChart,
  ProjectAnalysisOption,
  StrataOption,
  WorkforceRepresentationBarChart,
  WorkforceRepresentationDoughnutChart,
  WorkforceRepresentationStackedBarChart,
} from '~/constants/types/projectDataVisualizations'
import { useProjectStore } from '../projects'
import { useCompetitiveMovementsDataVisualizationsStore } from './competitiveMovementsDataVisualizations'
import type { ITilesData } from '~/constants/types/charts'
import { useNoncompetitiveMovementsDataVisualizationsStore } from './noncompetitiveMovementsDataVisualizations'
import { fileNetworkRequest, formatBytes, HNRV2 } from '~/helpers'
import { api } from '~/services'
import { AxiosError } from 'axios'
import { notifyFailure, notifySuccess } from '~/utils/notifications'
import { date } from 'quasar'
import type {
  ExecutiveSummaryChartKey,
  ExecutiveSummarySettingsSaveRequest,
} from '~/constants/types/executiveSummary'
import { openDownloadFileDialogWithData } from '~/helpers/file'
import * as htmlToImage from 'html-to-image'
import type ExecutiveSummaryCharts from '~/components/Dashboard/Reports/WFAExecutiveSummary/ExecutiveSummaryCharts.vue'
import {
  isCompetitivePromotionsAnalysisType,
  isLaborMarketAnalysisType,
  isNonCompetitiveMovementsAnalysisType,
  isSraHiringAnalysisType,
  isTotalSelectionsAnalysisType,
} from '~/helpers/wfa'
import { useAnalysisSelectionLifecycle } from '~/composables/chart/useAnalysisSelectionLifecycle'
import type { SelectionSnapshot } from './projectDataVisualizationsSelection'

interface ExecutiveSummaryTileData extends ITilesData {
  executiveSummaryId: ExecutiveSummaryChartKey
}

interface ExecutiveSummaryProgressInformation {
  fileName?: string
  fileSize?: string | undefined
  lastDownloaded?: string
  zipFileCreated?: boolean | null | undefined
  status: EnumerationsExecutiveSummaryStatus
  percent: number
  currentStepDescription?: string
  notificationID?: string
}

const defaultOption: AnalysisOption = {
  analysisID: '',
  analysisName: '',
  label: '',
  analysisCategoryID: 0,
  analysisTypeID: 0,
  proportionalAnalysisID: 0,
  proportionalAnalysisName: '',
}

export const useExecutiveSummaryStore = defineStore(
  STORE.EXECUTIVE_SUMMARY,
  () => {
    const summariesAPI = api.workforceAnalyticsSummaries
    const summariesFileAPI = api.workforceAnalyticsSummariesFile
    const dataVisualizationsAPI = api.workforceAnalyticsDataVisualizations

    const ProjectStore = useProjectStore()
    const ProjectOverviewDataVisualizationsStore =
      useProjectOverviewDataVisualizationsStore()
    const WorkforceInsightsDataVisualizationsStore =
      useWorkforceInsightsDataVisualizationsStore()
    const CompetitiveMovementsDataVisualizationsStore =
      useCompetitiveMovementsDataVisualizationsStore()
    const NoncompetitiveMovementsDataVisualizationsStore =
      useNoncompetitiveMovementsDataVisualizationsStore()

    const loading = ref({
      draft: false,
      initiateSummary: false,
    })

    const analysesToGenerate: Ref<
      Omit<CommandsStoreScreenshotsRequest, 'clientFilePath'>[]
    > = ref([])

    const generateSummaryPayload =
      computed<CommandsGenerateExecutiveSummaryRequest>(() => {
        return {
          analysesToGenerate:
            analysesToGenerate.value as CommandsStoreScreenshotsRequest[],
          projectID: ProjectStore.selectedProject?.projectID as string,
          regenerate: false,
        }
      })

    // Executive Summary Status/Progress

    const projectID = computed<string | undefined>(() => {
      return ProjectStore.selectedProject?.projectID
    })

    const summaryInformationIndex = ref<
      Record<string, ExecutiveSummaryProgressInformation | undefined>
    >({})

    const setSummaryInformation = (
      newValue: Partial<ExecutiveSummaryProgressInformation>,
      idToSet?: string
    ) => {
      const projectIDToSetFor = idToSet ?? projectID.value ?? undefined
      if (projectIDToSetFor) {
        summaryInformationIndex.value[projectIDToSetFor] = newValue
          ? {
              ...summaryInformationIndex.value[projectIDToSetFor],
              ...newValue,
              status:
                newValue.status ??
                summaryInformationIndex.value[projectIDToSetFor]?.status ??
                EnumerationsExecutiveSummaryStatus.NotStarted,
              percent:
                newValue.percent ??
                summaryInformationIndex.value[projectIDToSetFor]?.percent ??
                0,
            }
          : undefined
      }
    }

    const summaryInformationByProjectID = computed<
      ExecutiveSummaryProgressInformation | undefined
    >(() => {
      return projectID.value
        ? summaryInformationIndex.value[projectID.value]
        : undefined
    })

    const getSummaryInformationProjectIDByNotificationID = (
      notificationID: string
    ): string | undefined => {
      const projectIDForNotification = Object.keys(
        summaryInformationIndex.value
      ).find(
        (key) =>
          summaryInformationIndex.value[key]?.notificationID === notificationID
      )

      return projectIDForNotification
    }

    const summaryGenerationInProgress = computed<boolean>(() => {
      return (
        loading.value.initiateSummary ||
        summaryInformationByProjectID.value?.status ===
          EnumerationsExecutiveSummaryStatus.Running
      )
    })

    const isZipFileDownloadable = computed<boolean>(() => {
      return (
        !!projectID.value &&
        !!summaryInformationIndex.value[projectID.value]?.zipFileCreated &&
        !!summaryInformationIndex.value[projectID.value]?.notificationID
      )
    })

    // four selected analyses
    const selectedAnalyses = ref<{
      sraHiring: AnalysisOption | undefined
      sraCompetitivePromotions: AnalysisOption | undefined
      sraTotalSelections: AnalysisOption | undefined
      paNonCompetitivePromotions: AnalysisOption | undefined
      paLaborforceCalculations: AnalysisOption | undefined
    }>({
      sraHiring: undefined,
      sraCompetitivePromotions: undefined,
      sraTotalSelections: undefined,
      paNonCompetitivePromotions: undefined,
      paLaborforceCalculations: undefined,
    })

    const uniqueAnalysisName = ({
      options,
      newOption,
    }: {
      options: AnalysisOption[]
      newOption: AnalysisOption
    }) => {
      return !options
        .map((item) => item.analysisName)
        .includes(newOption.analysisName)
    }
    const analysisOptions = computed<
      Record<keyof typeof selectedAnalyses.value, AnalysisOption[]>
    >(() => {
      return ProjectStore.analysesForSelectedProject.reduce<{
        sraHiring: AnalysisOption[]
        sraCompetitivePromotions: AnalysisOption[]
        sraTotalSelections: AnalysisOption[]
        paNonCompetitivePromotions: AnalysisOption[]
        paLaborforceCalculations: AnalysisOption[]
      }>(
        (acc, option) => {
          const analysisOption = {
            ...option,
            label: option.analysisName,
          }
          if (
            isSraHiringAnalysisType(analysisOption) &&
            uniqueAnalysisName({
              options: acc.sraHiring,
              newOption: analysisOption,
            })
          ) {
            acc.sraHiring.push(analysisOption)
          }

          if (
            isCompetitivePromotionsAnalysisType(analysisOption) &&
            uniqueAnalysisName({
              options: acc.sraCompetitivePromotions,
              newOption: analysisOption,
            })
          ) {
            acc.sraCompetitivePromotions.push(analysisOption)
          }

          if (
            isTotalSelectionsAnalysisType(analysisOption) &&
            uniqueAnalysisName({
              options: acc.sraTotalSelections,
              newOption: analysisOption,
            })
          ) {
            acc.sraTotalSelections.push(analysisOption)
          }

          if (
            isNonCompetitiveMovementsAnalysisType(analysisOption) &&
            uniqueAnalysisName({
              options: acc.paNonCompetitivePromotions,
              newOption: analysisOption,
            })
          ) {
            acc.paNonCompetitivePromotions.push(analysisOption)
          }

          if (
            isLaborMarketAnalysisType(analysisOption) &&
            uniqueAnalysisName({
              options: acc.paLaborforceCalculations,
              newOption: analysisOption,
            })
          ) {
            acc.paLaborforceCalculations.push(analysisOption)
          }

          return acc
        },
        {
          sraHiring: [defaultOption],
          sraCompetitivePromotions: [defaultOption],
          sraTotalSelections: [defaultOption],
          paNonCompetitivePromotions: [defaultOption],
          paLaborforceCalculations: [defaultOption],
        }
      )
    })

    const selectedStrata = ref<
      Record<keyof typeof analysisOptions.value, StrataOption | undefined>
    >({
      sraHiring: undefined,
      sraCompetitivePromotions: undefined,
      sraTotalSelections: undefined,
      paNonCompetitivePromotions: undefined,
      paLaborforceCalculations: undefined,
    })

    const strataOptions = ref<
      Record<keyof typeof selectedAnalyses.value, StrataOption[]>
    >({
      sraHiring: [],
      sraCompetitivePromotions: [],
      sraTotalSelections: [],
      paNonCompetitivePromotions: [],
      paLaborforceCalculations: [],
    })

    const chartDataErrors = ref<
      Record<keyof typeof selectedAnalyses.value, boolean>
    >({
      sraHiring: false,
      sraCompetitivePromotions: false,
      sraTotalSelections: false,
      paNonCompetitivePromotions: false,
      paLaborforceCalculations: false,
    })

    const paNonCompetitivePromotionsSelection = computed<AnalysisSelection>(
      () => {
        return {
          analysis: selectedAnalyses.value.paNonCompetitivePromotions,
          strata: selectedStrata.value.paNonCompetitivePromotions,
          selectionReady: !!(
            selectedAnalyses.value.paNonCompetitivePromotions?.analysisID &&
            selectedStrata.value.paNonCompetitivePromotions?.strataFieldGUID &&
            !chartDataErrors.value.paNonCompetitivePromotions
          ),
        }
      }
    )

    const paLaborForceCalculationsSelection = computed<AnalysisSelection>(
      () => {
        return {
          analysis: selectedAnalyses.value.paLaborforceCalculations,
          strata: selectedStrata.value.paLaborforceCalculations,
          selectionReady: !!(
            selectedAnalyses.value.paLaborforceCalculations?.analysisID &&
            selectedStrata.value.paLaborforceCalculations?.strataFieldGUID &&
            !chartDataErrors.value.paLaborforceCalculations
          ),
        }
      }
    )

    const getSelectionSnapshot = ({
      analysisID,
      proportionalAnalysisTypeID,
      strataID,
    }: {
      analysisID: string | null
      proportionalAnalysisTypeID?: EnumerationsProportionalAnalyses | null
      strataID: string | null
    }): SelectionSnapshot => {
      return {
        projectID: projectID.value as string,
        analysisID: analysisID,
        proportionalAnalysisTypeID: proportionalAnalysisTypeID
          ? proportionalAnalysisTypeID
          : null,
        groupFieldID: null,
        strataID: strataID,
        strataValue: null,
        selectionVersion: 0,
      }
    }

    const resetSelectedAnalyses = () => {
      selectedAnalyses.value.sraHiring = undefined
      selectedAnalyses.value.sraCompetitivePromotions = undefined
      selectedAnalyses.value.paNonCompetitivePromotions = undefined
      selectedAnalyses.value.paLaborforceCalculations = undefined

      selectedStrata.value.sraHiring = undefined
      selectedStrata.value.sraCompetitivePromotions = undefined
      selectedStrata.value.paNonCompetitivePromotions = undefined
      selectedStrata.value.paLaborforceCalculations = undefined
    }

    const unlockInputs = ref(false)
    const disableInputs = computed(() => {
      return summaryGenerationInProgress.value && !unlockInputs.value
    })

    const sraHiringFrequencyCharts = ref<
      FrequencyOfStatisticalImpactChart[] | undefined
    >()
    const getSraHiringData = async (): Promise<boolean> => {
      sraHiringFrequencyCharts.value = undefined
      chartDataErrors.value.sraHiring = false

      if (!selectedAnalyses.value.sraHiring?.analysisID) {
        notifyFailure('No SRA Hiring analysis selected')
        return false
      }

      const selectionSnapshot = getSelectionSnapshot({
        analysisID: selectedAnalyses.value.sraHiring.analysisID,
        strataID: selectedStrata.value.sraHiring?.strataFieldGUID ?? null,
      })

      const chartData =
        await CompetitiveMovementsDataVisualizationsStore.getTilesAndBarCharts(
          selectionSnapshot
        )

      if (!chartData) {
        chartDataErrors.value.sraHiring = true
        return false
      }

      sraHiringFrequencyCharts.value =
        CompetitiveMovementsDataVisualizationsStore.buildFrequencyOfStatisticalImpactCharts(
          chartData,
          'Hiring'
        )

      return true
    }

    const sraCompetitivePromotionsFrequencyCharts = ref()
    const getSraCompetitivePromotionsData = async (): Promise<boolean> => {
      sraCompetitivePromotionsFrequencyCharts.value = undefined
      chartDataErrors.value.sraCompetitivePromotions = false

      if (!selectedAnalyses.value.sraCompetitivePromotions?.analysisID) {
        return false
      }

      const selectionSnapshot = getSelectionSnapshot({
        analysisID: selectedAnalyses.value.sraCompetitivePromotions.analysisID,
        strataID:
          selectedStrata.value.sraCompetitivePromotions?.strataFieldGUID ??
          null,
      })

      const sraCompetitivePromotionsData = await Promise.all([
        CompetitiveMovementsDataVisualizationsStore.getTilesAndBarCharts(
          selectionSnapshot
        ),

        ProjectOverviewDataVisualizationsStore.getData(selectionSnapshot),
      ])

      if (sraCompetitivePromotionsData.some((chartData) => !chartData)) {
        chartDataErrors.value.sraCompetitivePromotions = true
        return false
      }

      const [chartData] = sraCompetitivePromotionsData

      sraCompetitivePromotionsFrequencyCharts.value =
        CompetitiveMovementsDataVisualizationsStore.buildFrequencyOfStatisticalImpactCharts(
          chartData,
          'Promo'
        )

      return true
    }

    const sraTotalSelectionsFrequencyCharts = ref()
    const getSraTotalSelectionsData = async (): Promise<boolean> => {
      sraTotalSelectionsFrequencyCharts.value = undefined
      chartDataErrors.value.sraTotalSelections = false

      if (!selectedAnalyses.value.sraTotalSelections?.analysisID) {
        return false
      }

      const selectionSnapshot = getSelectionSnapshot({
        analysisID: selectedAnalyses.value.sraTotalSelections.analysisID,
        strataID:
          selectedStrata.value.sraTotalSelections?.strataFieldGUID ?? null,
      })

      const sraTotalSelectionsData = await Promise.all([
        CompetitiveMovementsDataVisualizationsStore.getTilesAndBarCharts(
          selectionSnapshot
        ),
        ProjectOverviewDataVisualizationsStore.getData(selectionSnapshot),
      ])

      if (sraTotalSelectionsData.some((chartData) => !chartData)) {
        chartDataErrors.value.sraTotalSelections = true
        return false
      }

      const [chartData] = sraTotalSelectionsData

      sraTotalSelectionsFrequencyCharts.value =
        CompetitiveMovementsDataVisualizationsStore.buildFrequencyOfStatisticalImpactCharts(
          chartData
        )

      return true
    }

    const nonCompetitiveTiles = ref<ExecutiveSummaryTileData[]>([])

    const getPANonCompetitivePromotionData = async (): Promise<boolean> => {
      chartDataErrors.value.paNonCompetitivePromotions = false

      if (
        !selectedAnalyses.value.paNonCompetitivePromotions?.analysisID ||
        !paNonCompetitivePromotionsSelection.value.selectionReady
      ) {
        return false
      }

      nonCompetitiveTiles.value = []

      const allAnalysisOptions = ProjectStore.formattedAnalysisOptions
      const relevantPAAnalyses: ProjectAnalysisOption[] =
        allAnalysisOptions.filter((analysis) => {
          const matchingAnalysisAsSelected =
            selectedAnalyses.value.paNonCompetitivePromotions?.analysisID ===
            analysis.analysisID
          const desiredPAAnalysisType: EnumerationsProportionalAnalyses[] = [
            EnumerationsProportionalAnalyses.NonCompetitivePromotionsAnalysis,
            EnumerationsProportionalAnalyses.AllTerminationsAnalysis,
            EnumerationsProportionalAnalyses.VoluntaryTerminationsAnalysis,
            EnumerationsProportionalAnalyses.InvoluntaryTerminationsAnalysis,
          ]
          const matchingPAType = desiredPAAnalysisType.includes(
            analysis.proportionalAnalysisID
          )
          return matchingAnalysisAsSelected && matchingPAType
        })

      const paTiles: ExecutiveSummaryTileData[] = []

      const success = await getWorkforceRepresentationCharts({
        analysis: selectedAnalyses.value.paNonCompetitivePromotions,
        strata: selectedStrata.value.paNonCompetitivePromotions,
      })

      if (!success) {
        chartDataErrors.value.paNonCompetitivePromotions = true
        return false
      }

      for (const analysis of relevantPAAnalyses) {
        // hit proportional analysis GET for tiles

        const selectionSnapshot = getSelectionSnapshot({
          analysisID: analysis.analysisID,
          proportionalAnalysisTypeID: analysis.proportionalAnalysisID,
          strataID:
            selectedStrata.value.paNonCompetitivePromotions?.strataFieldGUID ??
            null,
        })
        const chartData =
          await NoncompetitiveMovementsDataVisualizationsStore.getTilesAndBarCharts(
            selectionSnapshot
          )

        if (!chartData) {
          chartDataErrors.value.paNonCompetitivePromotions = true
          return false
        }

        const tiles =
          NoncompetitiveMovementsDataVisualizationsStore.transformTopLevelTileDataToTiles(
            chartData?.topLevelTileData
          )

        // for non comp selection
        // get other PA analyses in same analysisID
        // transform tiles, then filter to only desired values and push to tiles

        const includeStrataTile = (tile: ITilesData): boolean => {
          const strataName: string | undefined =
            selectedStrata.value.paNonCompetitivePromotions?.label

          if (!strataName) {
            return false
          }

          return tile.title.includes(strataName)
        }
        const filteredTiles = tiles.reduce<ExecutiveSummaryTileData[]>(
          (acc, tile) => {
            //  <!-- noncompetitive promo rate - Top Tile From Proportional Page -->
            // <!-- noncompetitive promotions - Top Tile From Proportional Page -->
            // <!-- noncompetitive promotions SigResultsDeptNonCompPromo - Top Tile From Proportional Page -->
            // <!-- noncompetitive promotions SigResultsJobNonCompPromo - Top Tile From Proportional Page -->
            if (
              analysis.proportionalAnalysisID ===
              EnumerationsProportionalAnalyses.NonCompetitivePromotionsAnalysis
            ) {
              if (tile.title === 'Total Promotions') {
                acc.push({
                  ...tile,
                  executiveSummaryId: 'NonCompPromoRate',
                })
              }
              // if (tile.title === 'Significant Results: Department Name') {
              //   acc.push({
              //     ...tile,
              //     executiveSummaryId: 'SigResultsDeptNonCompPromo',
              //   })
              // }
              const sigResultsNonCompPromoExecutiveSummaryId =
                'SigResultsJobNonCompPromo'

              if (includeStrataTile(tile)) {
                acc.push({
                  ...tile,
                  executiveSummaryId: sigResultsNonCompPromoExecutiveSummaryId,
                })
              }
            }

            // <!-- TermRate - Top Tile From Proportional Page -->
            // <!-- SigResultsDeptTerm - Top Tile From Proportional Page -->
            // <!-- SigResultsJobTerm - Top Tile From Proportional Page -->
            if (
              analysis.proportionalAnalysisID ===
              EnumerationsProportionalAnalyses.AllTerminationsAnalysis
            ) {
              if (tile.title === 'Total Terminations') {
                acc.push({
                  ...tile,
                  executiveSummaryId: 'TermRate',
                })
              }
              // if (tile.title === 'Significant Results: Department Name') {
              //   acc.push({
              //     ...tile,
              //     executiveSummaryId: 'SigResultsDeptTerm',
              //   })
              // }

              const sigResultsJobTermExecutiveSummaryId = 'SigResultsJobTerm'
              if (includeStrataTile(tile)) {
                acc.push({
                  ...tile,
                  executiveSummaryId: sigResultsJobTermExecutiveSummaryId,
                })
              }
            }

            // <!-- VolTermRate - Top Tile From Proportional Page -->
            // <!-- SigResultsDeptVolTerm - Top Tile From Proportional Page -->
            // <!-- SigResultsJobVolTerm - Top Tile From Proportional Page -->
            if (
              analysis.proportionalAnalysisID ===
              EnumerationsProportionalAnalyses.VoluntaryTerminationsAnalysis
            ) {
              if (tile.title === 'Total Terminations') {
                acc.push({
                  ...tile,
                  executiveSummaryId: 'VolTermRate',
                })
              }
              // if (tile.title === 'Significant Results: Department Name') {
              //   acc.push({
              //     ...tile,
              //     executiveSummaryId: 'SigResultsDeptVolTerm',
              //   })
              // }

              const sigResultsJobVolTermExecutiveSummaryId =
                'SigResultsJobVolTerm'
              if (includeStrataTile(tile)) {
                acc.push({
                  ...tile,
                  executiveSummaryId: sigResultsJobVolTermExecutiveSummaryId,
                })
              }
            }

            // <!-- InvolTermRate - Top Tile From Proportional Page -->
            // <!-- SigResultsDeptInvolTerm - Top Tile From Proportional Page -->
            if (
              analysis.proportionalAnalysisID ===
              EnumerationsProportionalAnalyses.InvoluntaryTerminationsAnalysis
            ) {
              if (tile.title === 'Total Terminations') {
                acc.push({
                  ...tile,
                  executiveSummaryId: 'InvolTermRate',
                })
              }
              // if (tile.title === 'Significant Results: Department Name') {
              //   acc.push({
              //     ...tile,
              //     executiveSummaryId: 'SigResultsDeptInvolTerm',
              //   })
              // }
              const sigResultsJobInvolTermExecutiveSummaryId =
                'SigResultsJobInvolTerm'
              if (includeStrataTile(tile)) {
                acc.push({
                  ...tile,
                  executiveSummaryId: sigResultsJobInvolTermExecutiveSummaryId,
                })
              }
            }
            return acc
          },
          []
        )

        paTiles.push(...filteredTiles)
      }

      nonCompetitiveTiles.value.push(...paTiles)

      const selectionSnapshot = getSelectionSnapshot({
        analysisID: null,
        strataID: null,
      })

      const overviewData = await ProjectOverviewDataVisualizationsStore.getData(
        selectionSnapshot
      )

      if (!overviewData) {
        chartDataErrors.value.paNonCompetitivePromotions = true
        return false
      }

      return true
    }

    const getWorkforceRepresentationCharts = async ({
      analysis,
      strata,
    }: {
      analysis: Omit<
        (typeof ProjectStore.formattedAnalysisOptions)[number],
        'uniqueID'
      >
      strata: (typeof selectedStrata.value)[keyof typeof selectedStrata.value]
    }): Promise<boolean> => {
      const selectionSnapshot = getSelectionSnapshot({
        analysisID: analysis.analysisID,
        strataID: strata?.strataFieldGUID ?? null,
      })

      const chartData =
        await WorkforceInsightsDataVisualizationsStore.getTilesAndBarCharts(
          selectionSnapshot
        )

      if (!chartData) {
        return false
      }

      const workforceRepresentationTiles: ExecutiveSummaryTileData[] =
        WorkforceInsightsDataVisualizationsStore.transformTopLevelTileDataToTiles(
          chartData?.tileData
        ).reduce<ExecutiveSummaryTileData[]>((acc, tile) => {
          if (tile.title === 'Hiring Rate') {
            acc.push({
              ...tile,
              executiveSummaryId: 'HireRate',
            })
          }

          if (tile.title === 'Overall Promotion Rate') {
            acc.push({
              ...tile,
              executiveSummaryId: 'PromotionRate',
            })
          }

          if (tile.title === 'Turnover Rate') {
            acc.push({
              ...tile,
              executiveSummaryId: 'TurnoverRate',
            })
          }

          return acc
        }, [])

      workforceRepresentationTilesByAnalysis.value[analysis.analysisID] =
        workforceRepresentationTiles
      payGradeChartsByAnalysis.value[analysis.analysisID] =
        getRelevantProtectedClassesFromWorkforceRepresentationCharts(
          WorkforceInsightsDataVisualizationsStore.transformPayGradeCharts(
            chartData
          )
        )
      payDecileChartsByAnalysis.value[analysis.analysisID] =
        getRelevantProtectedClassesFromWorkforceRepresentationCharts(
          WorkforceInsightsDataVisualizationsStore.transformPayDecileCharts(
            chartData
          )
        )
      overallTenureChartByAnalysis.value[analysis.analysisID] =
        WorkforceInsightsDataVisualizationsStore.transformOverallTenureChart(
          chartData
        )
      tenureChartsByAnalysis.value[analysis.analysisID] =
        getRelevantProtectedClassesFromWorkforceRepresentationCharts(
          WorkforceInsightsDataVisualizationsStore.transformTenureCharts(
            chartData
          )
        )
      totalRepresentationDoughnutsByAnalysis.value[analysis.analysisID] =
        getRelevantProtectedClassesFromWorkforceRepresentationCharts(
          WorkforceInsightsDataVisualizationsStore.transformOverviewDoughnutCharts(
            chartData
          )
        )

      return true
    }

    const getPALaborForceCalculationData = async (): Promise<boolean> => {
      chartDataErrors.value.paLaborforceCalculations = false

      if (
        !selectedAnalyses.value.paLaborforceCalculations ||
        !paLaborForceCalculationsSelection.value.selectionReady
      ) {
        return false
      }

      const success = await getWorkforceRepresentationCharts({
        analysis: selectedAnalyses.value.paLaborforceCalculations,
        strata: selectedStrata.value.paLaborforceCalculations,
      })

      if (!success) {
        chartDataErrors.value.paLaborforceCalculations = true
      }

      return success
    }

    const getStrataOptions = async ({
      analysisID,
    }: {
      analysisID: string
    }): Promise<StrataOption[]> => {
      const response = await HNRV2({
        fn: () =>
          dataVisualizationsAPI.apiSraDataVisualizationsFiltersAnalysisIDGet(
            analysisID
          ),
      })

      if (response instanceof AxiosError) {
        notifyFailure('Problem getting strata options')
        return []
      }

      return [
        { label: '', strataFieldGUID: '' },
        ...response.strataWithValues.map((strata) => {
          return {
            label: strata.strataDisplayName,
            strataFieldGUID: strata.strataFieldGUID!,
          }
        }),
      ]
    }

    const observationText = ref(['', '', '', ''])

    const competitivePromotionOverviewCard = computed<
      ExecutiveSummaryPersonnelActivityAnalysis | undefined
    >(() => {
      const paCard =
        ProjectOverviewDataVisualizationsStore.analyses.sraAnalyses?.find(
          (analysis) =>
            selectedAnalyses.value.sraCompetitivePromotions?.analysisID &&
            analysis.analysisID ===
              selectedAnalyses.value.sraCompetitivePromotions.analysisID
        )
      return paCard
        ? { ...paCard, executiveSummaryId: 'CompetitivePromo' }
        : undefined
    })
    const totalSelectionsOverviewCard = computed<
      ExecutiveSummaryPersonnelActivityAnalysis | undefined
    >(() => {
      const paCard =
        ProjectOverviewDataVisualizationsStore.analyses.sraAnalyses?.find(
          (analysis) =>
            selectedAnalyses.value.sraTotalSelections?.analysisID &&
            analysis.analysisID ===
              selectedAnalyses.value.sraTotalSelections.analysisID
        )
      return paCard
        ? { ...paCard, executiveSummaryId: 'TotalSelections' }
        : undefined
    })
    const nonCompetitivePromotionOverviewCard = computed<
      ExecutiveSummaryPersonnelActivityAnalysis | undefined
    >(() => {
      const paCard =
        ProjectOverviewDataVisualizationsStore.analyses.proportionalAnalyses.find(
          (analysis) => {
            return (
              selectedAnalyses.value.paNonCompetitivePromotions?.analysisID &&
              analysis.analysisID ===
                selectedAnalyses.value.paNonCompetitivePromotions.analysisID &&
              analysis.proportionalAnalysisID ===
                EnumerationsProportionalAnalyses.NonCompetitivePromotionsAnalysis
            )
          }
        )
      return paCard
        ? { ...paCard, executiveSummaryId: 'NonCompetitivePromo' }
        : undefined
    })

    const terminationsOverviewCard = computed<
      ExecutiveSummaryPersonnelActivityAnalysis | undefined
    >(() => {
      const paCard =
        ProjectOverviewDataVisualizationsStore.analyses.proportionalAnalyses.find(
          (analysis) => {
            return (
              selectedAnalyses.value.paNonCompetitivePromotions?.analysisID &&
              analysis.analysisID ===
                selectedAnalyses.value.paNonCompetitivePromotions.analysisID &&
              analysis.proportionalAnalysisID ===
                EnumerationsProportionalAnalyses.AllTerminationsAnalysis
            )
          }
        )
      return paCard
        ? { ...paCard, executiveSummaryId: 'TermSummary' }
        : undefined
    })

    const getRelevantProtectedClassesFromWorkforceRepresentationCharts = <
      T extends { protectedClass: string }
    >(
      charts: T[]
    ): T[] => {
      return charts.filter((chart) =>
        ['race', 'sex'].includes(chart.protectedClass.toLowerCase())
      )
    }

    const workforceRepresentationTilesByAnalysis = ref<
      Record<string, ExecutiveSummaryTileData[]>
    >({})
    const payGradeChartsByAnalysis = ref<
      Record<string, WorkforceRepresentationBarChart[]>
    >({})
    const payDecileChartsByAnalysis = ref<
      Record<string, WorkforceRepresentationBarChart[]>
    >({})
    const overallTenureChartByAnalysis = ref<
      Record<string, WorkforceRepresentationBarChart[]>
    >({})
    const tenureChartsByAnalysis = ref<
      Record<string, WorkforceRepresentationStackedBarChart[]>
    >({})
    const totalRepresentationDoughnutsByAnalysis = ref<
      Record<string, WorkforceRepresentationDoughnutChart[]>
    >({})

    const saved = ref<
      Record<
        'strataSelection',
        Record<keyof typeof selectedAnalyses.value, string | undefined>
      >
    >({
      strataSelection: {
        sraHiring: undefined,
        sraCompetitivePromotions: undefined,
        paNonCompetitivePromotions: undefined,
        paLaborforceCalculations: undefined,
        sraTotalSelections: undefined,
      },
    })

    const restoreSavedStrata = ({
      loadingState,
      prevLoadingState,
      analysisKey,
    }: {
      loadingState: boolean | undefined
      prevLoadingState: boolean | undefined
      analysisKey: keyof typeof selectedAnalyses.value
    }) => {
      const finishedLoading = prevLoadingState && !loadingState

      if (finishedLoading && !!saved.value.strataSelection[analysisKey]) {
        selectedStrata.value[analysisKey] =
          strataOptions.value[analysisKey].find((option) => {
            return (
              option?.strataFieldGUID ===
              saved.value.strataSelection[analysisKey]
            )
          }) ?? undefined

        saved.value.strataSelection[analysisKey] = undefined
      }
    }

    const sraHiringLifecycle = useAnalysisSelectionLifecycle({
      analysisId: computed(() => selectedAnalyses.value.sraHiring?.analysisID),

      strataId: computed(() => selectedStrata.value.sraHiring?.strataFieldGUID),

      strataOptions: toRef(strataOptions.value, 'sraHiring'),

      getStrataOptions: async (analysisID) =>
        await getStrataOptions({ analysisID }),

      getChartData: getSraHiringData,
    })

    watch(
      () => sraHiringLifecycle.loadingStrataOptions.value,
      (loadingState, prevLoadingState) => {
        restoreSavedStrata({
          analysisKey: 'sraHiring',
          loadingState,
          prevLoadingState,
        })
      }
    )

    const sraCompetitivePromotionsLifecycle = useAnalysisSelectionLifecycle({
      analysisId: computed(
        () => selectedAnalyses.value.sraCompetitivePromotions?.analysisID
      ),

      strataId: computed(
        () => selectedStrata.value.sraCompetitivePromotions?.strataFieldGUID
      ),

      strataOptions: toRef(strataOptions.value, 'sraCompetitivePromotions'),

      getStrataOptions: async (analysisID) =>
        await getStrataOptions({ analysisID }),

      getChartData: getSraCompetitivePromotionsData,
    })

    watch(
      () => sraCompetitivePromotionsLifecycle.loadingStrataOptions.value,
      (loadingState, prevLoadingState) => {
        restoreSavedStrata({
          analysisKey: 'sraCompetitivePromotions',
          loadingState,
          prevLoadingState,
        })
      }
    )

    const sraTotalSelectionsLifecycle = useAnalysisSelectionLifecycle({
      analysisId: computed(
        () => selectedAnalyses.value.sraTotalSelections?.analysisID
      ),

      strataId: computed(
        () => selectedStrata.value.sraTotalSelections?.strataFieldGUID
      ),

      strataOptions: toRef(strataOptions.value, 'sraTotalSelections'),

      getStrataOptions: async (analysisID) =>
        await getStrataOptions({ analysisID }),

      getChartData: getSraTotalSelectionsData,
    })

    watch(
      () => sraTotalSelectionsLifecycle.loadingStrataOptions.value,
      (loadingState, prevLoadingState) => {
        restoreSavedStrata({
          analysisKey: 'sraTotalSelections',
          loadingState,
          prevLoadingState,
        })
      }
    )

    const paNonCompetitivePromotionsLifecycle = useAnalysisSelectionLifecycle({
      analysisId: computed(
        () => paNonCompetitivePromotionsSelection.value.analysis?.analysisID
      ),

      strataId: computed(
        () => paNonCompetitivePromotionsSelection.value.strata?.strataFieldGUID
      ),

      strataOptions: toRef(strataOptions.value, 'paNonCompetitivePromotions'),

      getStrataOptions: async (analysisID) =>
        await getStrataOptions({ analysisID }),

      getChartData: async () => {
        return await getPANonCompetitivePromotionData()
      },
      enabled: computed(
        () => paNonCompetitivePromotionsSelection.value.selectionReady
      ),
      clearChartData: paNonCompetitivePromotionsSelection.value.clearChartData,
    })

    watch(
      () => paNonCompetitivePromotionsLifecycle.loadingStrataOptions.value,
      (loadingState, prevLoadingState) => {
        restoreSavedStrata({
          analysisKey: 'paNonCompetitivePromotions',
          loadingState,
          prevLoadingState,
        })
      }
    )

    const paLaborforceCalculationsLifecycle = useAnalysisSelectionLifecycle({
      analysisId: computed(
        () => paLaborForceCalculationsSelection.value.analysis?.analysisID
      ),

      strataId: computed(
        () => paLaborForceCalculationsSelection.value.strata?.strataFieldGUID
      ),

      strataOptions: toRef(strataOptions.value, 'paLaborforceCalculations'),

      getStrataOptions: async (analysisID) =>
        await getStrataOptions({ analysisID }),

      getChartData: async () => {
        return await getPALaborForceCalculationData()
      },

      enabled: computed(
        () =>
          !paNonCompetitivePromotionsSelection.value.selectionReady &&
          paLaborForceCalculationsSelection.value.selectionReady
      ),
      clearChartData: paLaborForceCalculationsSelection.value.clearChartData,
    })

    watch(
      () => paLaborforceCalculationsLifecycle.loadingStrataOptions.value,
      (loadingState, prevLoadingState) => {
        restoreSavedStrata({
          analysisKey: 'paLaborforceCalculations',
          loadingState,
          prevLoadingState,
        })
      }
    )

    const getSettings = async ({
      projectID,
      checkSummaryCompletion,
    }: {
      projectID: string
      checkSummaryCompletion?: boolean
    }) => {
      if (!checkSummaryCompletion) {
        observationText.value = []
        resetSelectedAnalyses()
      }

      const response = await HNRV2({
        fn: () =>
          summariesAPI.apiSraSummariesExecutiveSummarySettingsProjectIDGet(
            projectID
          ),
      })

      if (response instanceof AxiosError) {
        notifyFailure('Problem getting executive summary settings')
        return false
      }

      const selectedSraHiringAnalysis = analysisOptions.value.sraHiring.find(
        (option) => response.hiringAnalysisID === option.analysisID
      )
      const selectedSraCompetitivePromotionsAnalysis =
        analysisOptions.value.sraCompetitivePromotions.find(
          (option) =>
            response.competitivePromotionsAnalysisID === option.analysisID
        )
      const selectedPaNonCompetitivePromotionsAnalysis =
        analysisOptions.value.paNonCompetitivePromotions.find(
          (option) =>
            response.promoTermsTransfersAnalysisID === option.analysisID
        )
      const selectedPaLaborforceCalculationsAnalysis =
        analysisOptions.value.paLaborforceCalculations.find(
          (option) => response.laborforceAnalysisID === option.analysisID
        )
      const selectedSraTotalSelectionsAnalysis =
        analysisOptions.value.sraTotalSelections.find(
          (option) => response.totalSelectionsAnalysisID === option.analysisID
        )

      const restoreSelections = ({
        analysisKey,
        restoredAnalysis,
        restoredStrataGUID,
      }: {
        analysisKey: keyof typeof selectedAnalyses.value
        restoredAnalysis: AnalysisOption | undefined
        restoredStrataGUID: string | null | undefined
      }) => {
        if (restoredAnalysis) {
          selectedAnalyses.value[analysisKey] = restoredAnalysis
          saved.value.strataSelection[analysisKey] =
            restoredStrataGUID || undefined
        }
        return
      }

      const restoreSelectionConfig: Parameters<typeof restoreSelections>[0][] =
        [
          {
            analysisKey: 'sraHiring',
            restoredAnalysis: selectedSraHiringAnalysis,
            restoredStrataGUID: response.hiringAnalysisStrataFieldGUID,
          },
          {
            analysisKey: 'sraCompetitivePromotions',
            restoredAnalysis: selectedSraCompetitivePromotionsAnalysis,
            restoredStrataGUID: response.competitivePromotionStrataFieldGUID,
          },
          {
            analysisKey: 'paNonCompetitivePromotions',
            restoredAnalysis: selectedPaNonCompetitivePromotionsAnalysis,
            restoredStrataGUID: response.promoTermsTransfersStrataFieldGUID,
          },
          {
            analysisKey: 'paLaborforceCalculations',
            restoredAnalysis: selectedPaLaborforceCalculationsAnalysis,
            restoredStrataGUID: response.laborforceStrataFieldGUID,
          },
          {
            analysisKey: 'sraTotalSelections',
            restoredAnalysis: selectedSraTotalSelectionsAnalysis,
            restoredStrataGUID: response.totalSelectionsStrataFieldGUID,
          },
        ]

      restoreSelectionConfig.forEach((config) => {
        restoreSelections(config)
      })

      observationText.value = [
        response.dataObservations1 ? response.dataObservations1 : '',
        response.dataObservations2 ? response.dataObservations2 : '',
        response.dataObservations3 ? response.dataObservations3 : '',
        response.dataObservations4 ? response.dataObservations4 : '',
      ]

      const extractedNotificationID: string | undefined =
        response.notificationID || undefined

      if (
        response.statusID === EnumerationsExecutiveSummaryStatus.Running &&
        !extractedNotificationID
      ) {
        notifyFailure(
          'Problem retrieving executive summary status. You may not see progress updates, please try refreshing the page periodically for updates.'
        )
      }

      setSummaryInformation(
        {
          fileName: response.fileName ?? '',
          fileSize: response.fileSize
            ? formatBytes(response.fileSize)
            : undefined,
          lastDownloaded: response.lastDownloadDate
            ? date.formatDate(response.lastDownloadDate, 'MM-DD-YYYY')
            : undefined,
          zipFileCreated: response.zipFileCreated,
          percent: 0,
          currentStepDescription: '',
          status: response.statusID,
          notificationID: extractedNotificationID,
        },
        projectID
      )

      return true
    }

    const saveSettings = async () => {
      const payload: ExecutiveSummarySettingsSaveRequest = {
        projectID: ProjectStore.selectedProject!.projectID,
        hiringAnalysisID:
          selectedAnalyses.value.sraHiring?.analysisID || undefined,
        hiringAnalysisStrataFieldGUID:
          selectedStrata.value.sraHiring?.strataFieldGUID || undefined,
        competitivePromotionsAnalysisID:
          selectedAnalyses.value.sraCompetitivePromotions?.analysisID ||
          undefined,
        competitivePromotionStrataFieldGUID:
          selectedStrata.value.sraCompetitivePromotions?.strataFieldGUID ||
          undefined,
        promoTermsTransfersAnalysisID:
          selectedAnalyses.value.paNonCompetitivePromotions?.analysisID ||
          undefined,
        promoTermsTransfersStrataFieldGUID:
          selectedStrata.value.paNonCompetitivePromotions?.strataFieldGUID ||
          undefined,
        laborforceAnalysisID:
          selectedAnalyses.value.paLaborforceCalculations?.analysisID ||
          undefined,
        laborforceStrataFieldGUID:
          selectedStrata.value.paLaborforceCalculations?.strataFieldGUID ||
          undefined,
        totalSelectionsAnalysisID:
          selectedAnalyses.value.sraTotalSelections?.analysisID,
        totalSelectionsStrataFieldGUID:
          selectedStrata.value.sraTotalSelections?.strataFieldGUID,
        dataObservations1: observationText.value[0],
        dataObservations2: observationText.value[1],
        dataObservations3: observationText.value[2],
        dataObservations4: observationText.value[3],
      }

      const response = await HNRV2({
        fn: () =>
          summariesAPI.apiSraSummariesExecutiveSummarySettingsPost(payload),
      })

      if (response instanceof AxiosError) {
        notifyFailure('Problem saving executive summary settings')
        return false
      }

      return true
    }

    const exportChartImages = async ({
      payload,
    }: {
      payload: CommandsGenerateExecutiveSummaryRequest
    }): Promise<boolean> => {
      const response = await HNRV2({
        fn: () => summariesAPI.apiSraSummariesExecutiveSummaryPost(payload),
      })

      if (response instanceof AxiosError) {
        notifyFailure('Problem starting summary generation')
        return false
      }

      setSummaryInformation({
        currentStepDescription: '',
        fileName: undefined,
        fileSize: undefined,
        lastDownloaded: undefined,
        percent: 0,
        status: EnumerationsExecutiveSummaryStatus.Running,
        zipFileCreated: payload.regenerate
          ? summaryInformationByProjectID.value?.zipFileCreated ?? false
          : false,
        notificationID: response,
      })

      return true
    }

    const downloadExecutiveSummary = async (
      notificationID: string | undefined
    ): Promise<boolean> => {
      if (!notificationID) {
        return false
      }

      const response = await fileNetworkRequest({
        fn: () =>
          summariesFileAPI.apiSraSummariesExecutiveSummaryDownloadNotificationIDGet(
            notificationID
          ),
      })

      if (response instanceof AxiosError) {
        notifyFailure(
          'Problem downloading executive summary.  Please try again'
        )
        return false
      }

      openDownloadFileDialogWithData({ response })

      return true
    }

    const downloadExecutiveSummaryFiles = async (
      notificationID: string | undefined
    ): Promise<boolean> => {
      if (!notificationID) {
        return false
      }

      const response = await fileNetworkRequest({
        fn: () =>
          summariesFileAPI.apiSraSummariesExecutiveSummaryDownloadZipNotificationIDGet(
            notificationID
          ),
      })

      if (response instanceof AxiosError) {
        notifyFailure(
          'Problem downloading files for executive summary.  Please try again'
        )
        return false
      }

      openDownloadFileDialogWithData({ response })

      return true
    }

    const downloadReportSummary = async (
      notificationID: string | undefined
    ): Promise<boolean> => {
      if (!notificationID) {
        return false
      }

      const response = await fileNetworkRequest({
        fn: () =>
          summariesFileAPI.apiSraSummariesExecutiveSummaryDownloadReportNotificationIDGet(
            notificationID
          ),
      })

      if (response instanceof AxiosError) {
        notifyFailure(
          'Problem downloading executive report summary.  Please try again'
        )
        return false
      }

      openDownloadFileDialogWithData({ response })

      return true
    }

    const generateSummary = async (
      executiveSummaryChartsRef: InstanceType<
        typeof ExecutiveSummaryCharts
      > | null
    ) => {
      const exportChartImage = ({
        element,
        fontEmbedCSS,
      }: {
        element: HTMLElement
        fontEmbedCSS: string
      }) => {
        return htmlToImage.toPng(element, {
          skipFonts: false,
          fontEmbedCSS: fontEmbedCSS,
          // pixelRatio: 2,
        })
      }
      const getFontEmbedCSS = async (chartRefs: any[]) => {
        if (!chartRefs.length) {
          return ''
        }

        const chartElement: HTMLElement =
          '$el' in chartRefs[0]
            ? (chartRefs[0].$el as HTMLElement)
            : chartRefs[0]

        return htmlToImage.getFontEmbedCSS(chartElement)
      }

      try {
        loading.value.initiateSummary = true

        if (!executiveSummaryChartsRef) {
          return
        }

        unlockInputs.value = false

        const saveSettingsSuccess = await saveSettings()
        if (!saveSettingsSuccess) {
          return
        }

        analysesToGenerate.value = []

        for (const analysisID in executiveSummaryChartsRef.chartTemplateRefsByAnalysis) {
          const fontEmbedCSS = await getFontEmbedCSS(
            executiveSummaryChartsRef.chartTemplateRefsByAnalysis[analysisID]
          )

          const htmlImages: Record<string, string> = await [
            ...executiveSummaryChartsRef.chartTemplateRefsByAnalysis[
              analysisID
            ],
          ].reduce(async (images, chart, index) => {
            const acc = await images

            const key = chart.executiveSummaryId || `${index}`

            if (chart) {
              const element = '$el' in chart ? chart.$el : chart
              const png = await exportChartImage({ element, fontEmbedCSS })

              acc[key] = png
            }
            return acc
          }, Promise.resolve({}))

          analysesToGenerate.value.push({
            analysisID: analysisID,
            base64Images: htmlImages,
          })
        }

        const success = await exportChartImages({
          payload: generateSummaryPayload.value,
        })

        if (success) {
          notifySuccess(
            'Generated and uploaded images, summary generation in progress...'
          )
        }
      } finally {
        loading.value.initiateSummary = false
      }
    }

    const regeneratePresentation = async () => {
      try {
        loading.value.initiateSummary = true
        const success = await exportChartImages({
          payload: { ...generateSummaryPayload.value, regenerate: true },
        })

        if (success) {
          notifySuccess('Summary regeneration in progress...')
        }
      } finally {
        loading.value.initiateSummary = false
      }
    }

    return {
      loading,

      selectedAnalyses,
      selectedStrata,
      resetSelectedAnalyses,
      chartDataErrors,
      paNonCompetitivePromotionsSelection,
      paLaborForceCalculationsSelection,

      analysisOptions,
      strataOptions,
      unlockInputs,
      disableInputs,

      observationText,

      // get data functions
      getSraHiringData,
      getSraCompetitivePromotionsData,
      getPANonCompetitivePromotionData,
      getPALaborForceCalculationData,

      projectID,
      summaryInformationIndex,
      setSummaryInformation,
      summaryInformationByProjectID,
      getSummaryInformationProjectIDByNotificationID,
      summaryGenerationInProgress,
      isZipFileDownloadable,

      // tiles
      nonCompetitiveTiles,
      workforceRepresentationTiles: workforceRepresentationTilesByAnalysis,

      // charts
      sraHiringFrequencyCharts,
      sraCompetitivePromotionsFrequencyCharts,
      competitivePromotionOverviewCard,
      sraTotalSelectionsFrequencyCharts,
      totalSelectionsOverviewCard,
      getSraTotalSelectionsData,
      nonCompetitivePromotionOverviewCard,
      terminationsOverviewCard,
      payGradeCharts: payGradeChartsByAnalysis,
      payDecileCharts: payDecileChartsByAnalysis,
      overallTenureChart: overallTenureChartByAnalysis,
      tenureCharts: tenureChartsByAnalysis,
      totalRepresentationDoughnuts: totalRepresentationDoughnutsByAnalysis,

      getSettings,
      saveSettings,
      exportChartImages,
      downloadExecutiveSummary,
      downloadExecutiveSummaryFiles,
      downloadReportSummary,
      generateSummary,
      regeneratePresentation,
      getStrataOptions,
    }
  }
)
